import { Stack } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { v4 as uuidv4 } from 'uuid';
import { Notification } from './Notification';
import {
  NotificationContextProvider,
  ShowNotificationOptions,
} from './NotificationContext';
import { NotificationsContainer } from './NotificationsContainer';

interface NotificationProviderOptions {
  children: React.ReactNode;
}

interface NotificationObject extends ShowNotificationOptions {
  notificationId: string;
}

export function NotificationProvider({
  children,
}: NotificationProviderOptions) {
  const [notifications, setNotifications] = useState<NotificationObject[]>([]);

  const showNotification = useCallback(
    ({ contentComponent, props }: ShowNotificationOptions) => {
      const notificationId = uuidv4();

      setNotifications((notificationsState) => [
        ...notificationsState,
        {
          notificationId,
          contentComponent,
          props,
        },
      ]);

      return notificationId;
    },
    [setNotifications],
  );

  const showSuccessNotification = useCallback(
    ({ contentComponent, props }: ShowNotificationOptions) => {
      return showNotification({
        contentComponent,
        props: { ...props, severity: 'success' },
      });
    },
    [showNotification],
  );

  const showErrorNotification = useCallback(
    ({ contentComponent, props }: ShowNotificationOptions) => {
      return showNotification({
        contentComponent,
        props: { ...props, severity: 'error' },
      });
    },
    [showNotification],
  );

  const hideNotification = useCallback((notificationId: string) => {
    setNotifications((notificationsState) => {
      return notificationsState.filter(
        (notification) => notification.notificationId !== notificationId,
      );
    });
  }, []);

  const renderNotifications = () => {
    return notifications.map(
      ({ notificationId, contentComponent: Content, props }, index) => {
        return (
          <NotificationsContainer
            notificationType={props.notificationType}
            key={notificationId}
            sx={{
              zIndex: props.zIndex ?? 9999,
            }}
            leftOffset={props.leftOffset}
            notificationIndex={index}
            numberOfActiveNotifications={notifications.length}
          >
            <Stack
              spacing={1}
              direction="column"
              alignContent="center"
              justifyContent="center"
            >
              <Notification {...props} id={notificationId}>
                {typeof Content === 'string' ? (
                  Content
                ) : (
                  <Content notificationID={notificationId} />
                )}
              </Notification>
            </Stack>
          </NotificationsContainer>
        );
      },
    );
  };
  const notificationContextValue = useMemo(() => {
    return {
      showNotification,
      hideNotification,
      showSuccessNotification,
      showErrorNotification,
    };
  }, [
    showNotification,
    hideNotification,
    showSuccessNotification,
    showErrorNotification,
  ]);

  return (
    <NotificationContextProvider value={notificationContextValue}>
      {children}
      {createPortal(renderNotifications(), document.body)}
    </NotificationContextProvider>
  );
}
